import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { useNavigate } from 'react-router-dom'
import { ApiClient } from '../../services/api'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import DefaultDashboard from './DefaultDashboard'

interface DashboardStats {
  total_reservas: number
  reservas_confirmadas: number
  reservas_canceladas: number
  reservas_pendientes: number
  ingresos_confirmados: number
  ingresos_pagados: number
  tasa_ocupacion: number
  tasa_cancelacion: number
}

interface OcupacionEspacio {
  espacio_id: string
  espacio_nombre: string
  tipo_espacio: string
  total_noches: number
  noches_ocupadas: number
  tasa_ocupacion: number
  ingresos: number
}

interface DashboardData {
  estadisticas: DashboardStats
  ocupacion_espacios: OcupacionEspacio[]
  empresa_nombre: string
  total_espacios: number
  skip: number
  limit: number
  total_paginas: number
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [fechaDesde, setFechaDesde] = useState<string>('')
  const [fechaHasta, setFechaHasta] = useState<string>('')
  const [fechaDesdeSearch, setFechaDesdeSearch] = useState<string>('')  // Para debounce
  const [fechaHastaSearch, setFechaHastaSearch] = useState<string>('')  // Para debounce
  const [rangoActivo, setRangoActivo] = useState<string>('30dias')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paginaActual, setPaginaActual] = useState(1)
  const [limitEspacios] = useState(15)
  const { user: usuario, tienePermiso } = useAuth()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const user = usuario // Alias for existing references

  // Si el usuario no tiene permiso admin.administrar, mostrar DefaultDashboard (genérica)
  if (!tienePermiso('admin.administrar')) {
    return <DefaultDashboard />
  }

  // Inicializar fechas por defecto
  useEffect(() => {
    if (!fechaDesde || !fechaHasta) {
      const hoy = new Date()
      const hace30dias = new Date()
      hace30dias.setDate(hoy.getDate() - 30)

      const desde = hace30dias.toISOString().split('T')[0]
      const hasta = hoy.toISOString().split('T')[0]

      setFechaDesde(desde)
      setFechaHasta(hasta)
      setFechaDesdeSearch(desde)
      setFechaHastaSearch(hasta)
    }
  }, [])

  // Cargar datos del dashboard con fechas específicas
  const fetchDashboardData = useCallback(async (pagina: number = 1) => {
    if (!fechaDesdeSearch || !fechaHastaSearch) return

    try {
      setLoading(true)
      setError(null)

      // Construir parámetros
      const params: any = {}
      params.fecha_desde = fechaDesdeSearch
      params.fecha_hasta = fechaHastaSearch
      params.skip = (pagina - 1) * limitEspacios
      params.limit = limitEspacios

      const data = await ApiClient.get<DashboardData>('/api/admin-protected/dashboard', { params })
      setDashboardData(data)
      setPaginaActual(pagina)
    } catch (err: any) {
      // Si es 403 (Forbidden), significa que no tiene permisos - no loguear como error
      if (err.response?.status === 403) {
        // El permiso ya fue verificado arriba con tienePermiso('admin.administrar')
        // Si llegamos aquí, mostrar SupervisorDashboard
        setLoading(false)
        return
      }

      console.error('Error fetching dashboard:', err)

      // Si es error de autenticación, no mostrar el dashboard
      if (err.response?.data?.error_type === 'unauthorized') {
        // El ProtectedRoute debería manejar esto, pero por si acaso...
        setLoading(false)
        return
      }

      setError(err.response?.data?.detail || err.response?.data?.message || 'Error cargando dashboard')
    } finally {
      setLoading(false)
    }
  }, [fechaDesdeSearch, fechaHastaSearch, limitEspacios])

  // Debounce: esperar 500ms después de que el usuario deja de escribir para hacer fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (fechaDesde && fechaHasta) {
        setFechaDesdeSearch(fechaDesde)
        setFechaHastaSearch(fechaHasta)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [fechaDesde, fechaHasta])

  // Auto-fetch cuando cambian las fechas de búsqueda
  useEffect(() => {
    if (fechaDesdeSearch && fechaHastaSearch) {
      fetchDashboardData(1)  // Volver a página 1 cuando cambian las fechas
    }
  }, [fechaDesdeSearch, fechaHastaSearch, fetchDashboardData])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando dashboard..." />
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: `${colors.error}15`,
          borderColor: colors.error
        }}
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">⚠️</span>
          <div>
            <h3 className="font-bold" style={{ color: colors.error }}>Error al cargar dashboard</h3>
            <p style={{ color: colors.error }}>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!dashboardData) {
    return <div>No hay datos disponibles</div>
  }

  const { estadisticas, ocupacion_espacios, empresa_nombre } = dashboardData

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
      {/* Bienvenida */}
      <div className="text-white rounded-xl p-6" style={{
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark}, ${colors.primary})`
      }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">
              ¡Bienvenido, {user?.nombre_completo}!
            </h2>
            <p className="text-white/90">
              Empresa: {empresa_nombre || 'N/A'}
            </p>
          </div>
          <div className="text-6xl opacity-80">🏕️</div>
        </div>
      </div>

      {/* Controles de período */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Período de Análisis</h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label htmlFor="fecha-desde" className="text-sm font-medium text-gray-700">
              Desde:
            </label>
            <input
              id="fecha-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value)
                setRangoActivo('custom') // Cambio manual = rango personalizado
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': colors.primary } as any}
            />
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="fecha-hasta" className="text-sm font-medium text-gray-700">
              Hasta:
            </label>
            <input
              id="fecha-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value)
                setRangoActivo('custom') // Cambio manual = rango personalizado
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': colors.primary } as any}
            />
          </div>
          <button
            onClick={() => {
              const hoy = new Date()
              const hace30dias = new Date()
              hace30dias.setDate(hoy.getDate() - 30)
              setFechaDesde(hace30dias.toISOString().split('T')[0])
              setFechaHasta(hoy.toISOString().split('T')[0])
              setRangoActivo('30dias')
            }}
            className="px-4 py-2 rounded-lg text-sm transition-colors text-white"
            style={{
              backgroundColor: rangoActivo === '30dias' ? colors.primary : '#6b7280'
            }}
          >
            Últimos 30 días
          </button>
          <button
            onClick={() => {
              const hoy = new Date()
              const hace365dias = new Date()
              hace365dias.setDate(hoy.getDate() - 365)
              setFechaDesde(hace365dias.toISOString().split('T')[0])
              setFechaHasta(hoy.toISOString().split('T')[0])
              setRangoActivo('1ano')
            }}
            className="px-4 py-2 rounded-lg text-sm transition-colors text-white"
            style={{
              backgroundColor: rangoActivo === '1ano' ? colors.primary : '#6b7280'
            }}
          >
            Último año
          </button>
          <button
            onClick={() => {
              setFechaDesde('2020-01-01')
              const hoy = new Date()
              const futuro = new Date()
              futuro.setDate(hoy.getDate() + 365)
              setFechaHasta(futuro.toISOString().split('T')[0])
              setRangoActivo('historial')
            }}
            className="px-4 py-2 rounded-lg text-sm transition-colors text-white"
            style={{
              backgroundColor: rangoActivo === 'historial' ? colors.primary : '#6b7280'
            }}
          >
            Todo el historial
          </button>
        </div>
      </div>

      {/* Estadísticas principales */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${tienePermiso('pagos.ver') ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Reservas</p>
              <p className="text-3xl font-bold text-gray-900">{estadisticas?.total_reservas || 0}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: `${colors.primary}15` }}>
              <span className="text-2xl" style={{ color: colors.primary }}>📊</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Confirmadas</p>
              <p className="text-3xl font-bold" style={{ color: colors.success }}>
                {estadisticas?.reservas_confirmadas || 0}
              </p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: `${colors.success}15` }}>
              <span className="text-2xl" style={{ color: colors.success }}>✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Ocupación</p>
              <p className="text-3xl font-bold" style={{ color: colors.primary }}>{estadisticas?.tasa_ocupacion?.toFixed(1) || '0.0'}%</p>
            </div>
            <div className="p-3 rounded-full" style={{
              backgroundColor: `${colors.primary}15`
            }}>
              <span className="text-2xl" style={{ color: colors.primary }}>🏠</span>
            </div>
          </div>
        </div>

        {tienePermiso('pagos.ver') && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Ingresos Pagados</p>
                <p className="text-3xl font-bold" style={{ color: colors.primary }}>
                  ${estadisticas?.ingresos_pagados?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="p-3 rounded-full" style={{
                backgroundColor: `${colors.primary}15`
              }}>
                <span className="text-2xl" style={{ color: colors.primary }}>💰</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gráfico de ocupación por espacio */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Ocupación por Espacio</h3>

        {ocupacion_espacios && ocupacion_espacios.length > 0 ? (
          <>
            <div className="space-y-4">
              {ocupacion_espacios.map((espacio) => (
                <div key={espacio.espacio_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{espacio.espacio_nombre}</h4>
                      <p className="text-sm text-gray-600">{espacio.tipo_espacio}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold" style={{ color: colors.primary }}>
                        {espacio.tasa_ocupacion.toFixed(1)}%
                      </p>
                      <p className="text-sm text-gray-600">
                        ${espacio.ingresos.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Barra de progreso */}
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(espacio.tasa_ocupacion, 100)}%`,
                        background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`
                      }}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{espacio.noches_ocupadas} de {espacio.total_noches} unidades</span>
                    <span>ID: {espacio.espacio_id.slice(0, 8)}...</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Controles de Paginación */}
            {dashboardData && dashboardData.total_paginas > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-600">
                  Mostrando {(dashboardData.skip + 1)} a {Math.min(dashboardData.skip + dashboardData.limit, dashboardData.total_espacios)} de {dashboardData.total_espacios} espacios
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchDashboardData(paginaActual - 1)}
                    disabled={paginaActual === 1}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Página anterior"
                  >
                    ← Anterior
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({
                      length: dashboardData.total_paginas,
                    }).map((_, index) => {
                      const pagina = index + 1
                      const esPaginaActual = pagina === paginaActual
                      return (
                        <button
                          key={pagina}
                          onClick={() => fetchDashboardData(pagina)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            esPaginaActual
                              ? 'bg-gray-900 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                          title={`Ir a página ${pagina}`}
                        >
                          {pagina}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    onClick={() => fetchDashboardData(paginaActual + 1)}
                    disabled={paginaActual === dashboardData.total_paginas}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Página siguiente"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📊</div>
            <p>No hay datos de ocupación disponibles</p>
          </div>
        )}
      </div>

      {/* Acciones rápidas */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Acciones Rápidas</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg transition-colors"
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <span className="text-2xl">📅</span>
            <div className="text-left">
              <p className="font-semibold">Nueva Reserva</p>
              <p className="text-sm text-gray-600">Crear reserva manual</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg transition-colors"
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <span className="text-2xl">🏕️</span>
            <div className="text-left">
              <p className="font-semibold">Gestionar Espacios</p>
              <p className="text-sm text-gray-600">Editar espacios y fotos</p>
            </div>
          </button>

          <button
            className="flex items-center space-x-3 p-4 border-2 rounded-lg transition-colors"
            style={{
              borderColor: '#d1d5db'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <span className="text-2xl">📈</span>
            <div className="text-left">
              <p className="font-semibold">Ver Reportes</p>
              <p className="text-sm text-gray-600">Ingresos y estadísticas</p>
            </div>
          </button>
        </div>
      </div>

        </div>
      </div>
    </div>
  )
}

export default AdminDashboard